(() => {
  const TAG_NAME = 'rtd-learning-companion';

  if (customElements.get(TAG_NAME)) return;

  class RtdLearningCompanion extends HTMLElement {
    constructor() {
      super();

      this.timerFrame = null;
      this.progressStartedAt = null;
      this.progressElapsed = 0;
      this.programmaticScrollTimer = null;
      this.scrollSettleTimer = null;
      this.currentIndex = 0;
      this.programmaticScroll = false;
      this.userScrolling = false;
      this.isHovered = false;
      this.isFocused = false;
      this.editorPaused = false;
      this.initialized = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleProductInfoToggle = this.handleProductInfoToggle.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.gallery = this.querySelector('[data-rtd-learning-gallery]');
      this.slides = Array.from(this.querySelectorAll('[data-rtd-learning-slide]'));
      this.progressFill = this.querySelector('[data-rtd-learning-progress-fill]');
      this.duration = this.getDuration();
      this.autoRotate = this.dataset.autoRotate !== 'false';
      this.currentIndex = 0;
      this.progressStartedAt = null;
      this.progressElapsed = 0;
      this.programmaticScroll = false;
      this.userScrolling = false;
      this.isHovered = false;
      this.isFocused = false;
      this.editorPaused = false;
      this.initialized = true;

      this.addEventListener('click', this.handleProductInfoToggle);

      if (!this.gallery || this.slides.length < 2) return;

      this.motionQuery = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : { matches: false };

      this.gallery.addEventListener('scroll', this.handleScroll, { passive: true });
      this.gallery.addEventListener('pointerenter', this.handlePointerEnter);
      this.gallery.addEventListener('pointerleave', this.handlePointerLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      window.addEventListener('resize', this.handleResize);

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      } else if (this.motionQuery.addListener) {
        this.motionQuery.addListener(this.handleMotionChange);
      }

      this.currentIndex = this.getClosestSlideIndex();
      this.updateActiveSlide();
      this.resetProgress();
      this.startTimerIfAllowed();
    }

    disconnectedCallback() {
      this.stopTimer();

      if (this.scrollSettleTimer !== null) {
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = null;
      }

      if (this.programmaticScrollTimer !== null) {
        window.clearTimeout(this.programmaticScrollTimer);
        this.programmaticScrollTimer = null;
      }

      if (this.gallery) {
        this.gallery.removeEventListener('scroll', this.handleScroll);
        this.gallery.removeEventListener('pointerenter', this.handlePointerEnter);
        this.gallery.removeEventListener('pointerleave', this.handlePointerLeave);
      }

      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('click', this.handleProductInfoToggle);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      window.removeEventListener('resize', this.handleResize);

      if (this.motionQuery) {
        if (this.motionQuery.removeEventListener) {
          this.motionQuery.removeEventListener('change', this.handleMotionChange);
        } else if (this.motionQuery.removeListener) {
          this.motionQuery.removeListener(this.handleMotionChange);
        }
      }

      this.initialized = false;
    }

    getDuration() {
      const configuredSpeed = Number.parseFloat(this.dataset.changeSlidesSpeed);
      const speed = Number.isFinite(configuredSpeed)
        ? Math.min(9, Math.max(3, configuredSpeed))
        : 5;

      return speed * 1000;
    }

    now() {
      return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    }

    isReducedMotion() {
      return Boolean(this.motionQuery?.matches);
    }

    canAutoplay() {
      return Boolean(
        this.autoRotate &&
          !this.isReducedMotion() &&
          !this.isHovered &&
          !this.isFocused &&
          !this.editorPaused &&
          !this.programmaticScroll &&
          !this.userScrolling
      );
    }

    startTimerIfAllowed() {
      if (!this.initialized || this.slides.length < 2 || !this.canAutoplay()) {
        this.stopTimer();
        return;
      }

      if (this.timerFrame !== null) return;

      this.progressStartedAt = this.now() - this.progressElapsed;
      this.timerFrame = window.requestAnimationFrame(this.tick);
    }

    stopTimer() {
      if (this.timerFrame !== null) {
        window.cancelAnimationFrame(this.timerFrame);
        this.timerFrame = null;
      }

      if (this.progressStartedAt !== null) {
        this.progressElapsed = Math.min(
          this.duration,
          Math.max(0, this.now() - this.progressStartedAt)
        );
        this.progressStartedAt = null;
        this.updateProgress(this.progressElapsed / this.duration);
      }
    }

    tick(timestamp) {
      this.timerFrame = null;

      if (!this.canAutoplay()) {
        this.stopTimer();
        return;
      }

      if (this.progressStartedAt === null) {
        this.progressStartedAt = timestamp - this.progressElapsed;
      }

      this.progressElapsed = Math.min(this.duration, Math.max(0, timestamp - this.progressStartedAt));
      this.updateProgress(this.progressElapsed / this.duration);

      if (this.progressElapsed >= this.duration) {
        this.advanceSlide();
        return;
      }

      this.timerFrame = window.requestAnimationFrame(this.tick);
    }

    advanceSlide() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.scrollToSlide(nextIndex);
      this.startTimerIfAllowed();
    }

    scrollToSlide(index, { behavior = 'smooth', resetProgress = true } = {}) {
      if (!this.gallery || this.slides.length < 2) return;

      const targetIndex = (index + this.slides.length) % this.slides.length;
      const targetSlide = this.slides[targetIndex];
      this.currentIndex = targetIndex;
      this.updateActiveSlide();

      if (resetProgress) this.resetProgress();

      this.programmaticScroll = true;
      if (this.programmaticScrollTimer !== null) {
        window.clearTimeout(this.programmaticScrollTimer);
      }

      const left = this.getSlideScrollLeft(targetSlide);
      this.gallery.scrollTo({
        left,
        behavior: this.isReducedMotion() ? 'auto' : behavior
      });

      this.programmaticScrollTimer = window.setTimeout(() => {
        this.programmaticScroll = false;
        this.programmaticScrollTimer = null;
        this.syncActiveSlide();
        this.resetProgress();
        this.startTimerIfAllowed();
      }, behavior === 'smooth' && !this.isReducedMotion() ? 700 : 60);
    }

    getSlideScrollLeft(slide) {
      const galleryRect = this.gallery.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return Math.max(0, this.gallery.scrollLeft + slideRect.left - galleryRect.left);
    }

    getClosestSlideIndex() {
      if (!this.gallery || this.slides.length === 0) return 0;

      const galleryRect = this.gallery.getBoundingClientRect();
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - galleryRect.left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    syncActiveSlide() {
      const nextIndex = this.getClosestSlideIndex();
      if (nextIndex === this.currentIndex) return;

      this.currentIndex = nextIndex;
      this.updateActiveSlide();
      this.resetProgress();
    }

    updateActiveSlide() {
      this.slides?.forEach((slide, index) => {
        if (index === this.currentIndex) {
          slide.setAttribute('aria-current', 'true');
        } else {
          slide.removeAttribute('aria-current');
        }
      });
    }

    resetProgress() {
      this.progressElapsed = 0;
      this.progressStartedAt = null;
      this.updateProgress(0);
    }

    updateProgress(ratio) {
      if (!this.progressFill) return;

      const clampedRatio = Math.min(1, Math.max(0, ratio));
      this.progressFill.style.transform = `scaleX(${clampedRatio})`;
    }

    handleScroll() {
      if (!this.initialized || this.programmaticScroll) return;

      this.userScrolling = true;
      this.stopTimer();
      this.syncActiveSlide();

      if (this.scrollSettleTimer !== null) {
        window.clearTimeout(this.scrollSettleTimer);
      }

      this.scrollSettleTimer = window.setTimeout(() => {
        this.scrollSettleTimer = null;
        this.userScrolling = false;
        this.syncActiveSlide();
        this.resetProgress();
        this.startTimerIfAllowed();
      }, 180);
    }

    handlePointerEnter() {
      this.isHovered = true;
      this.stopTimer();
    }

    handlePointerLeave() {
      this.isHovered = false;
      this.startTimerIfAllowed();
    }

    handleFocusIn() {
      this.isFocused = true;
      this.stopTimer();
    }

    handleFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;

      this.isFocused = false;
      this.startTimerIfAllowed();
    }

    handleProductInfoToggle(event) {
      const toggle = event.target.closest?.('[data-product-info-drawer-item-toggle]');
      if (toggle && this.contains(toggle)) {
        event.preventDefault();
        this.toggleProductInfoItem(toggle);
        return;
      }

      const header = event.target.closest?.('[data-product-info-drawer-item-header]');
      if (!header || !this.contains(header)) return;
      if (
        event.target.closest?.(
          '.rtd-product-info-drawer__item-purchase, .rtd-product-info-drawer__item-image, .rtd-product-info-drawer__item-image-placeholder',
        )
      )
        return;

      const headerToggle = header.querySelector('[data-product-info-drawer-item-toggle]');
      if (!headerToggle) return;

      event.preventDefault();
      this.toggleProductInfoItem(headerToggle);
    }

    toggleProductInfoItem(toggle) {
      const bodyId = toggle.getAttribute('aria-controls');
      const body = bodyId ? document.getElementById(bodyId) : null;
      if (!body || !this.contains(body)) return;

      const nextExpanded = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(nextExpanded));
      toggle.setAttribute('aria-label', nextExpanded ? 'Hide product details' : 'Show product details');
      body.hidden = !nextExpanded;
    }

    handleMotionChange() {
      if (this.isReducedMotion()) {
        this.stopTimer();
        this.resetProgress();
      } else {
        this.startTimerIfAllowed();
      }
    }

    handleBlockSelect(event) {
      const target = event.target;
      const slide = target && target.closest ? target.closest('[data-rtd-learning-slide]') : null;
      if (!slide || !this.contains(slide)) return;

      this.editorPaused = true;
      this.stopTimer();
      this.scrollToSlide(this.slides.indexOf(slide), { behavior: 'auto' });
    }

    handleBlockDeselect(event) {
      const target = event.target;
      const slide = target && target.closest ? target.closest('[data-rtd-learning-slide]') : null;
      if (!slide || !this.contains(slide)) return;

      this.editorPaused = false;
      this.resetProgress();
      this.startTimerIfAllowed();
    }

    handleResize() {
      if (!this.userScrolling && !this.programmaticScroll) {
        this.syncActiveSlide();
      }
    }
  }

  customElements.define(TAG_NAME, RtdLearningCompanion);
})();
